import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Attestation } from "model/attestion.model";
import { TarificationSessionKey } from "model/tarification-session-key.model";
import { ETarResult } from "model/e-tar.result";
import { DeleteTarifiedItemActionType } from "store/types/tarification.types";
import { TarifiedItem } from "model/tarified-item.model";
import { User } from "model/user.model";
import LoadingScreen from "main/LoadingScreen";
import { Col, Row } from "@cc/react-components/Grid";
import Icon from "@cc/react-components/Icon";
import Spinner from "@cc/react-components/Spinner";
import Card from "@cc/react-components/Card";
import { TarificationApplicationSession } from "model/tarification.model";
import { TFunction } from "i18next";
import { Logo } from "../../components";

export interface Props {
  tSession?: TarificationSessionKey;
  deleteTarifiedItem: DeleteTarifiedItemActionType;
  attestation: Attestation;
  tApplicationSession: TarificationApplicationSession;
  user: User;
  eTarResult?: ETarResult;
  canUseHelena: boolean;
  onEditTarifiedItem: (tarifiedItem: TarifiedItem) => void;
}

const renderNomenclatures = (
  tSession: TarificationSessionKey,
  t: TFunction,
  deleteTarifiedItem: DeleteTarifiedItemActionType,
  attestation: Attestation,
  tApplicationSession: TarificationApplicationSession,
  user: User,
  showError: boolean,
  setShowError: (show: boolean) => void,
  onEditTarifiedItem: (tarifiedItem: TarifiedItem) => void,
  eTarResult?: ETarResult
) => {
  return (
    <>
      {tSession &&
        tSession.tarifiedItems &&
        tSession.tarifiedItems.map((tarifiedItem, key) => (
          <Row key={key}>
            <Col xs={4}>
              <p className="font-weight-bold my-3">{tarifiedItem.code}</p>
              <p
                onClick={() => onEditTarifiedItem(tarifiedItem)}
                className="font-weight-bold mb-3 text-muted"
              >
                {`€${tarifiedItem.honorarium}`}
                <Icon size={"1x"} type="edit" />
              </p>
            </Col>
            <Col xs={8}>
              <p className="font-weight-bold my-3">
                {tarifiedItem.prestationCodeText}
              </p>
            </Col>
          </Row>
        ))}
      {attestation.eAttest && (
        <Row>
          <Col xs={4}>
            <p className="font-weight-bold my-2">
              {t("tarificationOverview.eAttest")}
            </p>
          </Col>
        </Row>
      )}
      {attestation.eFact && (
        <Row>
          <Col xs={4}>
            <p className="font-weight-bold my-2">
              {t("tarificationOverview.eFact")}
            </p>
          </Col>
          <Col xs={8}>
            <p className="font-weight-bold my-2">
              <Icon
                size={"1x"}
                type="success-circle"
                className="text-success"
              />
            </p>
          </Col>
        </Row>
      )}
      {attestation.eFact && (
        <Row>
          <Col xs={4}>
            <p className="font-weight-bold my-2">eTar</p>
          </Col>
          <Col xs={8}>
            {eTarResult && eTarResult.error && (
              <>
                <p className="my-2" onClick={() => setShowError(!showError)}>
                  <Icon size={"1x"} type="help" className="text-danger" />
                </p>
                {showError && (
                  <p className="text-danger" style={{ margin: "0.3em 0" }}>
                    {eTarResult.error}
                  </p>
                )}
              </>
            )}
            {eTarResult &&
              eTarResult.brokenRules.length > 0 &&
              eTarResult.error == null && (
                <>
                  <p className="my-2" onClick={() => setShowError(!showError)}>
                    <Icon size={"1x"} type="help" className="text-danger" />
                  </p>
                  {showError && (
                    <p className="m-0 text-danger">
                      {eTarResult.brokenRules
                        .map((it) => `[${it.code}] : ${it.message}`)
                        .join("\n")}
                    </p>
                  )}
                </>
              )}
            {eTarResult &&
              eTarResult.brokenRules.length === 0 &&
              eTarResult.error == null && (
                <p className="my-2">
                  <Icon
                    size={"1x"}
                    type="success-circle"
                    className="text-success"
                  />
                </p>
              )}
            {eTarResult === undefined && (
              <div className="my-2">
                <Spinner size="sm" animation="brand" />
              </div>
            )}
          </Col>
        </Row>
      )}
    </>
  );
};

const OverviewCard = ({
  attestation,
  deleteTarifiedItem,
  tSession,
  tApplicationSession,
  user,
  eTarResult,
  onEditTarifiedItem,
  canUseHelena,
}: Props) => {
  const [t] = useTranslation();
  const [showError, setShowError] = useState(false);

  return (
    <Card>
      <Card.Body>
        <Card.Title>
          <>
            {canUseHelena && (
              <div className="float-right">
                <Logo logo="/images/helena_icon_active.png" />
              </div>
            )}
            <div className="d-flex align-items-center">
              <Icon size={"2x"} type="sort" />
              <h1 className="font-weight-bold m-0 ml-2">
                {t("attestation.title")}
              </h1>
            </div>
          </>
        </Card.Title>
        {!!tSession ? (
          <>
            <section>
              {renderNomenclatures(
                tSession,
                t,
                deleteTarifiedItem,
                attestation,
                tApplicationSession,
                user,
                showError,
                setShowError,
                onEditTarifiedItem,
                eTarResult
              )}
            </section>
            <section>{renderPrices(tSession, t)}</section>
          </>
        ) : (
          <div className="p-5">
            <LoadingScreen />
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

const renderPrices = (tSession: TarificationSessionKey, t: TFunction) => {
  return (
    <>
      <Row>
        <Col>
          <h4 className="font-weight-bold mb-3">{t("attestation.total")}</h4>
        </Col>
        <Col>
          <h4 className="font-weight-bold mb-3 text-right">
            {"€ " + tSession.totalValue}
          </h4>
        </Col>
      </Row>
      <Row>
        <Col>
          <h4 className="font-weight-bold m-0">
            {t("attestation.totalPatient")}
          </h4>
        </Col>
        <Col>
          <h4 className="font-weight-bold m-0 text-right">
            {"€ " + tSession.totalPatientContribution}
          </h4>
        </Col>
      </Row>
    </>
  );
};

export default OverviewCard;
